import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { newsletterSchema } from "@/lib/validation";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Client-side validation first
      const validated = newsletterSchema.parse({ email });
      
      // Submit via edge function for server-side validation
      const { data, error } = await supabase.functions.invoke('submit-newsletter', {
        body: { email: validated.email },
      });

      if (error) throw new Error('Anmeldung fehlgeschlagen');

      if (data?.error === 'already_subscribed') {
        toast({
          title: "Bereits registriert",
          description: "Diese E-Mail-Adresse ist bereits für den Newsletter angemeldet.",
          variant: "destructive",
        });
        return;
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      toast({
        title: "Erfolgreich angemeldet",
        description: "Vielen Dank für Ihre Newsletter-Anmeldung!",
      });

      setEmail("");
    } catch (error: any) {
      toast({
        title: "Fehler",
        description: error.message || "Anmeldung fehlgeschlagen",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-gradient-to-b from-secondary to-secondary/80 pt-16 pb-8 border-t border-border/50">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <h3 className="text-xl font-bold mb-4 bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
              OMN SEO Tools
            </h3>
            <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
              Professionelle SEO-Werkzeuge für den Schweizer Markt.
            </p>
            <p className="text-sm text-muted-foreground">
              <a href="mailto:info@omnseotools.ch" className="hover:text-primary transition-colors">
                info@omnseotools.ch
              </a>
            </p>
          </div>
          
          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-foreground mb-4">
              Navigation
            </h4>
            <ul className="space-y-3">
              <li>
                <a href="/" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Startseite
                </a>
              </li>
              <li>
                <a href="/#tools" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Tools
                </a>
              </li>
              <li>
                <a href="/about" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Über Uns
                </a>
              </li>
              <li>
                <a href="/contact" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Kontakt
                </a>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-foreground mb-4">
              Rechtliches
            </h4>
            <ul className="space-y-3">
              <li>
                <a href="/privacy" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Datenschutz
                </a>
              </li>
              <li>
                <a href="/terms" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Nutzungsbedingungen
                </a>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-foreground mb-4">
              Newsletter
            </h4>
            <p className="text-sm text-muted-foreground mb-4">
              Bleiben Sie auf dem Laufenden mit SEO-Tipps und Updates.
            </p>
            <form onSubmit={handleSubmit} className="space-y-3">
              <Input
                type="email"
                placeholder="Ihre E-Mail-Adresse"
                className="bg-background/50 border-border/50 focus:border-primary"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                maxLength={255}
              />
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "..." : "Anmelden"}
              </Button>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-border/50">
          <p className="text-center text-sm text-muted-foreground">
            © {new Date().getFullYear()} OMN SEO Tools. Alle Rechte vorbehalten.
          </p>
        </div>
      </div>
    </footer>
  );
};
